import {
  GIFT_CARD_PRESET_AMOUNTS,
  GIFT_CARD_MIN_AMOUNT,
  GIFT_CARD_MAX_AMOUNT,
  GIFT_CARD_DELIVERY_MAX_DAYS,
  type GiftCardPromotionPreview,
} from '@yoyoclub/shared';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';
import { giftCardApi } from '../services/giftCardApi';

const MIN_DOLLARS = GIFT_CARD_MIN_AMOUNT / 100;
const MAX_DOLLARS = GIFT_CARD_MAX_AMOUNT / 100;

function dollars(cents: number): string {
  return (cents / 100).toFixed(2).replace(/\.00$/, '');
}

export default function GiftCardPurchase() {
  const { t, i18n } = useTranslation('giftCard');
  const { user, isAuthenticated, getToken } = useAuth();

  // Promotion (site-wide on load, or a promo-code result after Apply) + its tiers.
  const [promo, setPromo] = useState<GiftCardPromotionPreview | null>(null);
  const [selectedTierId, setSelectedTierId] = useState<string | null>(null);

  // Plain-amount path (used when no tier is selected).
  const [amountCents, setAmountCents] = useState<number>(GIFT_CARD_PRESET_AMOUNTS[2]);
  const [customDollars, setCustomDollars] = useState('');

  const [purchaserName, setPurchaserName] = useState(user?.name ?? '');
  const [purchaserEmail, setPurchaserEmail] = useState('');
  const [isGift, setIsGift] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [giftMessage, setGiftMessage] = useState('');
  const [deliverAt, setDeliverAt] = useState('');

  // Bounds for the date input. Computed once per mount; PT day boundary
  // edge cases (~few hours/year) get rejected at the backend.
  const { minDeliverAt, maxDeliverAt } = useMemo(() => {
    const today = new Date();
    const isoDay = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const max = new Date(today);
    max.setDate(max.getDate() + GIFT_CARD_DELIVERY_MAX_DAYS);
    return { minDeliverAt: isoDay(today), maxDeliverAt: isoDay(max) };
  }, []);

  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState<string | null>(null);
  const [applyingCode, setApplyingCode] = useState(false);

  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    giftCardApi
      .getActivePromotion()
      .then((p) => setPromo(p))
      .catch(() => setPromo(null));
  }, []);

  const selectedTier = promo?.tiers.find((tr) => tr.id === selectedTierId) ?? null;

  // Custom amount is integer dollars only; drop any decimal part before stripping.
  const effectiveAmount = customDollars ? parseInt(customDollars, 10) * 100 : amountCents;
  const amountValid =
    Number.isFinite(effectiveAmount) &&
    effectiveAmount >= GIFT_CARD_MIN_AMOUNT &&
    effectiveAmount <= GIFT_CARD_MAX_AMOUNT;

  // Summary numbers depend on which path is active.
  const payCents = selectedTier ? selectedTier.payAmount : effectiveAmount;
  const valueCents = selectedTier ? selectedTier.faceValue : effectiveAmount;

  const handleApplyCode = async () => {
    setPromoError(null);
    const code = promoCode.trim();
    if (!code) return;
    try {
      setApplyingCode(true);
      const result = await giftCardApi.getActivePromotion(code);
      if (!result || result.tiers.length === 0) {
        setPromoError(t('purchase.errGeneric'));
        return;
      }
      setPromo(result);
      setSelectedTierId(result.tiers[0]?.id ?? null);
    } catch (err) {
      setPromoError(err instanceof Error ? err.message : t('purchase.errGeneric'));
    } finally {
      setApplyingCode(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedTier && !amountValid) {
      setError(t('purchase.errAmount', { min: MIN_DOLLARS, max: MAX_DOLLARS }));
      return;
    }
    if (!agreed) {
      setError(t('purchase.errAgree'));
      return;
    }
    if (!isAuthenticated && !purchaserEmail) {
      setError(t('purchase.errEmail'));
      return;
    }

    try {
      setSubmitting(true);
      const token = isAuthenticated ? await getToken() : undefined;
      const usingPromoCode = promo?.triggerType === 'PROMO_CODE';
      const { checkoutUrl } = await giftCardApi.createCheckout(
        {
          ...(selectedTier ? { promotionTierId: selectedTier.id } : { faceValue: effectiveAmount }),
          purchaserEmail: isAuthenticated ? (user?.email ?? '') : purchaserEmail,
          purchaserName: purchaserName.trim() || undefined,
          isGift,
          recipientEmail: isGift ? recipientEmail : undefined,
          recipientName: isGift ? recipientName : undefined,
          giftMessage: isGift ? giftMessage : undefined,
          deliverAt: isGift && deliverAt ? deliverAt : undefined,
          promoCode: selectedTier && usingPromoCode ? promoCode.trim() : undefined,
          locale: i18n.language,
        },
        token
      );
      window.location.href = checkoutUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : t('purchase.errGeneric'));
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 pt-6 pb-10">
      <Helmet>
        <title>{t('meta.titlePurchase')}</title>
        <meta name="description" content={t('meta.descriptionPurchase')} />
      </Helmet>
      <div className="flex items-start justify-between gap-3 mb-2">
        <h1 className="text-3xl font-bold text-gray-900">{t('purchase.title')}</h1>
        <Link
          to="/gift-cards/redeem"
          className="mt-1 text-sm font-medium text-primary border border-primary rounded-full px-3 py-1.5 hover:bg-primary/5 whitespace-nowrap"
        >
          {t('purchase.redeemLink')}
        </Link>
      </div>
      <p className="text-gray-600 mb-6">{t('purchase.subtitle')}</p>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-6 space-y-6">
        {/* Promotion tiers (pay X get Y) */}
        {promo && promo.tiers.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {promo.description || promo.name}
            </label>
            <p className="text-xs text-gray-500 mb-3">{t('purchase.offersTitle')}</p>
            <div className="grid grid-cols-2 gap-3">
              {promo.tiers.map((tr) => {
                const selected = selectedTierId === tr.id;
                return (
                  <button
                    key={tr.id}
                    type="button"
                    onClick={() => setSelectedTierId(selected ? null : tr.id)}
                    className={`text-left p-3 rounded-lg border ${
                      selected
                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                        : 'border-gray-300'
                    }`}
                  >
                    <p className="font-semibold text-gray-900">
                      {t('purchase.tierValue', { value: dollars(tr.faceValue) })}
                    </p>
                    <p className="text-sm text-gray-600">
                      {t('purchase.tierPay', { pay: dollars(tr.payAmount) })}
                    </p>
                    <p className="text-xs font-medium text-green-700">
                      {t('purchase.tierBonus', { bonus: dollars(tr.faceValue - tr.payAmount) })}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Plain amount selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('purchase.amountTitle')}
          </label>
          <div className="grid grid-cols-4 gap-2 mb-3">
            {GIFT_CARD_PRESET_AMOUNTS.map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => {
                  setAmountCents(a);
                  setCustomDollars('');
                  setSelectedTierId(null);
                }}
                className={`py-2 rounded-lg border font-semibold ${
                  !selectedTier && !customDollars && amountCents === a
                    ? 'border-primary bg-primary text-white'
                    : 'border-gray-300 text-gray-700'
                }`}
              >
                ${a / 100}
              </button>
            ))}
          </div>
          <input
            type="text"
            inputMode="numeric"
            value={customDollars}
            onChange={(e) => {
              // Integer dollars only: drop the decimal part, then strip non-digits.
              setCustomDollars(e.target.value.split('.')[0].replace(/[^0-9]/g, ''));
              setSelectedTierId(null);
            }}
            placeholder={t('purchase.customPlaceholder', { min: MIN_DOLLARS, max: MAX_DOLLARS })}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-primary focus:border-primary ${
              !selectedTier && customDollars && !amountValid
                ? 'border-red-400 focus:ring-red-200 focus:border-red-400'
                : 'border-gray-300'
            }`}
          />
          {!selectedTier && customDollars && !amountValid && (
            <p className="text-xs text-red-600 mt-1">
              {t('purchase.errAmount', { min: MIN_DOLLARS, max: MAX_DOLLARS })}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('purchase.yourName')}
          </label>
          <input
            type="text"
            value={purchaserName}
            onChange={(e) => setPurchaserName(e.target.value)}
            maxLength={80}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
          />
          <p className="text-xs text-gray-500 mt-1">{t('purchase.yourNameHint')}</p>
        </div>

        {!isAuthenticated && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('purchase.yourEmail')}
            </label>
            <input
              type="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={purchaserEmail}
              onChange={(e) => setPurchaserEmail(e.target.value.toLowerCase())}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
            />
          </div>
        )}

        <label className="flex items-center gap-2">
          <input type="checkbox" checked={isGift} onChange={(e) => setIsGift(e.target.checked)} />
          <span className="text-sm text-gray-700">{t('purchase.isGift')}</span>
        </label>

        {isGift && (
          <div className="space-y-3">
            <input
              type="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value.toLowerCase())}
              placeholder={t('purchase.recipientEmail')}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
            <input
              type="text"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              placeholder={t('purchase.recipientName')}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
            <textarea
              value={giftMessage}
              onChange={(e) => setGiftMessage(e.target.value)}
              placeholder={t('purchase.messagePlaceholder')}
              maxLength={500}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              rows={3}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('purchase.deliveryDate')}
              </label>
              <input
                type="date"
                value={deliverAt}
                min={minDeliverAt}
                max={maxDeliverAt}
                onChange={(e) => setDeliverAt(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
              <p className="text-xs text-gray-500 mt-1">{t('purchase.deliveryDateHint')}</p>
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('purchase.promoLabel')}
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono uppercase"
            />
            <button
              type="button"
              onClick={handleApplyCode}
              disabled={applyingCode || !promoCode.trim()}
              className="px-4 rounded-lg border border-primary text-primary font-semibold disabled:opacity-50"
            >
              {t('purchase.apply')}
            </button>
          </div>
          {promoError && <p className="text-sm text-red-600 mt-1">{promoError}</p>}
          {promo?.triggerType === 'PROMO_CODE' && !promoError && (
            <p className="text-sm text-green-700 mt-1">{t('purchase.codeApplied')}</p>
          )}
        </div>

        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-1"
          />
          <span className="text-sm text-gray-600">
            {t('purchase.agreePre')}
            <a href="/gift-cards/terms" target="_blank" className="text-primary underline">
              {t('purchase.termsLink')}
            </a>
            {t('purchase.agreePost')}
          </span>
        </label>

        {(selectedTier || amountValid) && (
          <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
            {t('purchase.summary', { pay: dollars(payCents), value: dollars(valueCents) })}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || (!selectedTier && !amountValid)}
          className="w-full py-3 px-4 rounded-lg font-semibold bg-primary text-white hover:bg-primary-dark disabled:opacity-60"
        >
          {submitting ? t('purchase.submitting') : t('purchase.submit')}
        </button>
      </form>
    </div>
  );
}
